import { Fragment, useEffect, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRight, Clock, ExternalLink, MapPin, AlertCircle } from 'lucide-react';
import type { components } from '@/types/api.generated';
import { formatRelativeEta, formatTime } from '@lib/datetime';
import type { RoutePlanningStop } from '../map/RoutePlanningMap';
import {
  minutesSinceLastSeen,
  UNASSIGNED_COLOR,
  type MappedDriverRoute,
  type MappedDriverTask,
  type RouteMonitoringJoinResult,
} from '../hooks/useRouteMonitoringJoin';

type MissingStop = components['schemas']['RoutePlanningStops']['missing'][number];
type MissingReason = MissingStop['reason'];

const MISSING_REASON_LABELS = {
  order_not_found: 'routePlanning.missingReason.order_not_found',
  no_address: 'routePlanning.missingReason.no_address',
  unresolved: 'routePlanning.missingReason.unresolved',
} as const satisfies Record<MissingReason, string>;

/** Status dot per Onfleet task state; the label stays foreground (ADR-0043). */
const TASK_STATE_DOT = {
  assigned: 'bg-status-assigned',
  active: 'bg-status-in-progress',
  completed: 'bg-success',
} as const satisfies Record<MappedDriverTask['state'], string>;

/**
 * Three rows in this sidebar are the same control — a filter result, a driver
 * task, a member job — because all three answer one click, or one Enter/Space,
 * by focusing a single stop on the map. What each row SHOWS differs, so only
 * the props that make it a control are shared.
 */
function activationProps(activate: (() => void) | null) {
  return {
    role: activate ? ('button' as const) : undefined,
    tabIndex: activate ? 0 : undefined,
    onClick: () => activate?.(),
    onKeyDown: activate
      ? (event: KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            activate();
          }
        }
      : undefined,
  };
}

/**
 * A row that stands for one delivery job opens that job — its own, never the
 * driver task's. Focusing the stop and navigating away are two intents, so
 * this link is the row's sibling, not its child.
 */
function JobLink({ jobId }: { jobId: string }) {
  const { t } = useTranslation();
  return (
    <Link
      to={`/dispatch/jobs/${jobId}`}
      aria-label={t('routePlanning.openJobDetail')}
      className="shrink-0 text-accent hover:underline"
    >
      <ExternalLink className="h-3 w-3" aria-hidden="true" />
    </Link>
  );
}

/**
 * Progress comes from the joined task list alone — the same list the map draws
 * — so the sidebar can never claim more finished stops than the route holds.
 */
function driverProgress(driver: MappedDriverRoute): {
  completed: number;
  pending: number;
  total: number;
} {
  const completed = driver.tasks.filter((task) => task.state === 'completed').length;
  return {
    completed,
    pending: driver.tasks.length - completed,
    total: driver.tasks.length,
  };
}

/**
 * A name an operator can actually read, or nothing.
 *
 * `RoutePlanningStop['order_name']` is `z.string()`, so an empty one
 * type-checks. Empty has to read as absent, or `rowLabel`'s `??` stops on it
 * and the row shows `routePlanning.unknownOrder` for a task whose Onfleet name
 * is right there, and `matchableOrderNames` offers a name nobody can type.
 */
function presentName(name: string | null | undefined): string | null {
  return name !== null && name !== undefined && name !== '' ? name : null;
}

/**
 * One order, one spelling (#2253).
 *
 * A row is named after the STOP it stands for, because that is the name the
 * map popup beside it shows: the D1 job name, `composeJobName(orderName,
 * deliveryDate)` -> `#92694HGS-0916`. The Onfleet task's metadata name
 * (`#92694HGS`) is the fallback, for the rows that reached no stop — a
 * consolidated task's own row, which stands for no single job (ADR-0026 D2),
 * and a task whose id is in none of the selected date's stops.
 */
export function rowLabel(task: MappedDriverTask, stop: RoutePlanningStop | null): string | null {
  return presentName(stop?.order_name) ?? presentName(task.orderName);
}

/**
 * Every spelling of this task's orders that the filter will answer to.
 *
 * Operators hold both: the Onfleet name off a driver's phone and the suffixed
 * job name off the map popup or a picking list. Either has to find the row —
 * the filter's own placeholder gives the suffixed form as its example. The
 * stops are listed one by one rather than joined because a consolidated task's
 * second member must be as findable as its first.
 */
export function matchableOrderNames(task: MappedDriverTask): string[] {
  return [task.orderName, ...task.stops.map((stop) => stop.order_name)]
    .map(presentName)
    .filter((name): name is string => name !== null);
}

/**
 * An unassigned stop answers to the one name it has: its own. It carries no
 * Onfleet task, so there is no second spelling for it to be found by.
 */
export function matchableStopNames(stop: RoutePlanningStop): string[] {
  const name = presentName(stop.order_name);
  return name === null ? [] : [name];
}

/**
 * The filter's one rule, stated once for every kind of row it searches: an
 * operator types the digits he has off a phone or a picking list, so any of
 * the row's names containing them — in either case — is a hit.
 */
function matchesQuery(names: string[], query: string): boolean {
  return names.some((name) => name.toLowerCase().includes(query));
}

/**
 * The one thing this component can decide about a driver task that reached no
 * stop: its task id is not among the stops the selected date returned. Every
 * other cause an operator might wonder about — cancelled, ungeocodable, created
 * seconds ago — needs facts only the backend holds, so the row does not guess.
 */
function UnlinkedReason({ className = '' }: { className?: string }) {
  const { t } = useTranslation();
  const hint = t('routePlanning.unlinked.notInTodayStopsHint');
  return (
    // `role="note"`, because an `aria-label` on a role-less span is not
    // reliably exposed: the label the operator most needs is the long
    // explanation, and a plain span may surface only the short visible text.
    <span
      role="note"
      className={`text-[10px] text-muted-foreground ${className}`}
      title={hint}
      aria-label={hint}
    >
      {t('routePlanning.unlinked.notInTodayStops')}
    </span>
  );
}

/** One filter result is one delivery job, not one driver task. */
interface FilterMatch {
  driver: MappedDriverRoute;
  task: MappedDriverTask;
  stop: RoutePlanningStop | null;
}

export interface RouteSidebarProps {
  monitoringJoin: RouteMonitoringJoinResult;
  missing: MissingStop[];
  isToday: boolean;
  selectedStop?: RoutePlanningStop | null;
  selectedDriverId?: string | null;
  onSelectDriver?: (workerId: string) => void;
  // The map has to know WHICH driver owns the stop before it can highlight the
  // route it belongs to, so the owning driver travels with the stop.
  onSelectStop?: (stop: RoutePlanningStop, driverId?: string) => void;
  className?: string;
}

export function RouteSidebar({
  monitoringJoin,
  missing,
  isToday,
  selectedStop,
  selectedDriverId = null,
  onSelectDriver,
  onSelectStop,
  className = '',
}: RouteSidebarProps) {
  const { t } = useTranslation();
  const [openDrivers, setOpenDrivers] = useState<Record<string, boolean>>({});
  const [filterText, setFilterText] = useState('');

  const toggleDriver = (workerId: string) => {
    // Rows start collapsed, so an untouched driver has no entry here and the
    // toggle has to flip that default rather than `undefined`.
    setOpenDrivers((prev) => ({ ...prev, [workerId]: !(prev[workerId] ?? false) }));
  };

  const openDriver = (workerId: string) => {
    setOpenDrivers((prev) => ({ ...prev, [workerId]: true }));
  };

  // A driver can also become selected from the map or from a task row, so the
  // "show me his stops" reaction belongs to the selection itself, not to the
  // click handler of one of the ways to make it.
  useEffect(() => {
    if (selectedDriverId === null) return;
    openDriver(selectedDriverId);
  }, [selectedDriverId]);

  const filterQuery = filterText.trim().toLowerCase();
  // The input that takes this query only exists while there are drivers to
  // search, so when the last one drops off the board it unmounts and what was
  // typed into it can no longer be read or cleared. Text nobody can see is not
  // a filter: with nothing to filter with, the page is simply not filtering.
  const isFiltering = monitoringJoin.drivers.length > 0 && filterQuery.length > 0;
  const filterMatches: FilterMatch[] = isFiltering
    ? monitoringJoin.drivers.flatMap((driver) =>
        driver.tasks
          .filter((task) => matchesQuery(matchableOrderNames(task), filterQuery))
          // Members of a consolidated task focus different stops, so they
          // cannot share one result row.
          .flatMap((task): FilterMatch[] =>
            task.stops.length > 0
              ? task.stops.map((stop) => ({ driver, task, stop }))
              : [{ driver, task, stop: null }],
          ),
      )
    : [];

  // The filter asks one question — "where is this order" — and a stop nobody is
  // carrying is one of its possible answers. A group that ignored the filter
  // would answer a different question, at length, directly under a line saying
  // no driver task matched.
  const unassignedMatches = isFiltering
    ? monitoringJoin.unassignedStops.filter((stop) =>
        matchesQuery(matchableStopNames(stop), filterQuery),
      )
    : monitoringJoin.unassignedStops;

  const totals = monitoringJoin.drivers.reduce(
    (acc, driver) => {
      const progress = driverProgress(driver);
      return {
        completed: acc.completed + progress.completed,
        pending: acc.pending + progress.pending,
        total: acc.total + progress.total,
      };
    },
    { completed: 0, pending: 0, total: 0 },
  );

  return (
    <aside
      className={`flex flex-col divide-y overflow-y-auto rounded-lg border bg-card text-card-foreground ${className}`}
      aria-label={t('routePlanning.sidebarTitle')}
    >
      {/* 1. Header & Summary */}
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-semibold">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span>{t('routePlanning.sidebarTitle')}</span>
          </div>
          {isToday && (
            <span className="text-xs text-muted-foreground">{t('dispatch.eta.todayOnly')}</span>
          )}
        </div>

        {!isToday ? (
          <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
            <p>{t('routePlanning.todayOnlyNotice')}</p>
          </div>
        ) : monitoringJoin.drivers.length > 0 ? (
          <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            <span>{t('dispatch.eta.pendingCount', { count: totals.pending })}</span>
            <span>{t('dispatch.eta.completedCount', { count: totals.completed })}</span>
            <span className="font-medium text-foreground">
              {t('dispatch.eta.totalCount', { count: totals.total })}
            </span>
          </div>
        ) : (
          <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
            {t('dispatch.eta.empty')}
          </div>
        )}
      </div>

      {/* 2. Driver task filter — answers "where is order #…?" across every driver */}
      {isToday && monitoringJoin.drivers.length > 0 && (
        <div className="p-2">
          <input
            type="text"
            value={filterText}
            onChange={(event) => setFilterText(event.target.value)}
            onKeyDown={(event) => {
              // Escape in a search box means "drop what I typed". The section
              // listens for Escape on the window to drop the driver selection,
              // which is a different intent, so this one stops here.
              if (event.key !== 'Escape') return;
              event.stopPropagation();
              setFilterText('');
            }}
            aria-label={t('routePlanning.filter.placeholder')}
            placeholder={t('routePlanning.filter.placeholder')}
            className="w-full rounded-md border bg-background px-2.5 py-1.5 text-xs outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
      )}

      {/* 3. Filter results replace the route list while the operator is searching */}
      {isToday && monitoringJoin.drivers.length > 0 && isFiltering && (
        <div className="divide-y overflow-y-auto flex-1 text-xs">
          {filterMatches.length === 0
            ? // The stops nobody is carrying are searched too, and the group
              // below shows what it found, so this line is the whole answer only
              // when that group found nothing either.
              unassignedMatches.length === 0 && (
                <p className="p-4 text-center text-muted-foreground">
                  {t('routePlanning.filter.noMatch')}
                </p>
              )
            : filterMatches.map(({ driver, task, stop }) => {
                // Same rule as the route list: a row with no mapped stop has
                // nothing to show on the map, so it is not a control.
                const selectResult =
                  stop !== null && onSelectStop
                    ? () => {
                        setFilterText('');
                        openDriver(driver.workerId);
                        onSelectStop(stop, driver.workerId);
                      }
                    : null;

                return (
                  <div
                    key={`${driver.workerId}-${task.taskId}-${stop?.job_id ?? 'unlinked'}`}
                    {...activationProps(selectResult)}
                    className={`flex items-center justify-between gap-2 p-2.5 transition-colors ${
                      selectResult ? 'cursor-pointer hover:bg-accent/15' : 'opacity-75'
                    }`}
                  >
                    <div className="flex min-w-0 items-center gap-1.5">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: driver.color }}
                      />
                      <div className="min-w-0">
                        <span className="block truncate font-medium">
                          {rowLabel(task, stop) || t('routePlanning.unknownOrder')}
                        </span>
                        <span className="block truncate text-muted-foreground">
                          {driver.workerName}
                        </span>
                        {task.stops.length === 0 && (
                          <UnlinkedReason className="mt-0.5 block truncate" />
                        )}
                      </div>
                    </div>
                    <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                      {t(`dispatch.eta.state.${task.state}`)}
                    </span>
                  </div>
                );
              })}
        </div>
      )}

      {/* 4. Drivers Route List */}
      {isToday && monitoringJoin.drivers.length > 0 && !isFiltering && (
        <div className="divide-y overflow-y-auto flex-1 p-2 space-y-2">
          {monitoringJoin.drivers.map((driver) => {
            const isOpen = openDrivers[driver.workerId] ?? false;
            const { completed: completedCount, total: totalCount } = driverProgress(driver);
            const progressPct =
              totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
            const dutyLabel = !driver.onDuty
              ? t('routePlanning.driver.offDuty')
              : driver.timeLastSeen !== null
                ? t('routePlanning.driver.lastSeen', {
                    minutes: minutesSinceLastSeen(driver.timeLastSeen),
                  })
                : null;

            const isDriverSelected = selectedDriverId === driver.workerId;
            // Every other driver steps back while one of them is under
            // inspection — they stay on screen, they just stop competing.
            const isDriverDimmed = selectedDriverId !== null && !isDriverSelected;

            return (
              <div
                key={driver.workerId}
                className={`rounded-lg border bg-background/50 overflow-hidden shadow-xs ${
                  isDriverDimmed ? 'is-dimmed opacity-50' : ''
                }`}
              >
                {/* Driver summary header. Selecting a driver and reading his
                    stops are different intents, so they are different controls:
                    the name selects, the chevron expands. */}
                <div className="flex items-stretch justify-between gap-2">
                  <button
                    type="button"
                    aria-pressed={isDriverSelected}
                    onClick={() => onSelectDriver?.(driver.workerId)}
                    className={`flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 p-3 text-left transition-colors ${
                      isDriverSelected ? 'bg-accent/15' : 'hover:bg-muted/30'
                    }`}
                  >
                    <span
                      className="h-3 w-3 rounded-full shrink-0 shadow-xs"
                      style={{ backgroundColor: driver.color }}
                    />
                    <div className="min-w-0">
                      <p className="truncate font-medium text-sm text-foreground">
                        {driver.workerName}
                      </p>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span>
                          {t('dispatch.eta.progress', {
                            completed: completedCount,
                            total: totalCount,
                          })}
                        </span>
                        {dutyLabel !== null && (
                          <>
                            <span aria-hidden="true">·</span>
                            <span className="truncate">{dutyLabel}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    aria-expanded={isOpen}
                    // Deliberately name-free: the driver's own name belongs to
                    // the select control next to it, which must stay the only
                    // button an operator (or a test) can find by that name.
                    aria-label={t('routePlanning.driver.toggleTasks')}
                    onClick={() => toggleDriver(driver.workerId)}
                    className="flex shrink-0 cursor-pointer items-center gap-3 p-3 transition-colors hover:bg-muted/30"
                  >
                    <div className="hidden sm:flex flex-col items-end gap-1">
                      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${progressPct}%`,
                            backgroundColor: driver.color,
                          }}
                        />
                      </div>
                      <span className="text-[10px] text-muted-foreground font-mono">
                        {progressPct}%
                      </span>
                    </div>
                    <ChevronRight
                      className={`h-4 w-4 text-muted-foreground transition-transform ${
                        isOpen ? 'rotate-90' : ''
                      }`}
                    />
                  </button>
                </div>

                {/* Driver task list */}
                {isOpen && (
                  <div className="border-t bg-muted/10 divide-y text-xs">
                    {driver.tasks.length === 0 ? (
                      <p className="p-3 text-center text-muted-foreground">
                        {t('dispatch.eta.allDone')}
                      </p>
                    ) : (
                      driver.tasks.map((task: MappedDriverTask, index: number) => {
                        // A row speaks for the stop it owns. A consolidated
                        // task owns none itself — its member rows below do —
                        // so it is neither a control nor a dimmed dead end.
                        const isConsolidated = task.stops.length > 1;
                        const rowStop = isConsolidated ? null : (task.stops[0] ?? null);
                        const isSelected = selectedStop && rowStop?.job_id === selectedStop.job_id;
                        const relEta = formatRelativeEta(task.estimatedCompletionTime);
                        // Only a row that resolves to a stop does anything, so
                        // only that row advertises itself as an operable control.
                        const stop = rowStop;
                        const selectStop =
                          stop !== null && onSelectStop
                            ? () => onSelectStop(stop, driver.workerId)
                            : null;

                        return (
                          <Fragment key={task.taskId}>
                            <div
                              data-testid="task-row"
                              className={`flex items-center gap-2 p-2.5 transition-colors ${
                                rowStop
                                  ? 'cursor-pointer hover:bg-accent/15'
                                  : isConsolidated
                                    ? ''
                                    : 'opacity-75'
                              } ${isSelected ? 'bg-accent/25 font-medium' : ''}`}
                            >
                              {/* The row focuses the stop on the map and the job
                                link navigates away: two intents, so they are
                                siblings. A control nested inside another one is
                                reachable by neither keyboard nor screen reader. */}
                              {/* "He is on his 5th call" is a sentence about THIS
                                driver's round, so the count starts at his
                                first stop and starts over for the next driver.
                                It is derived for the screen and stored
                                nowhere. */}
                              <span
                                data-testid="task-sequence"
                                className="w-5 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground"
                              >
                                {index + 1}
                              </span>

                              <div
                                {...activationProps(selectStop)}
                                className="flex min-w-0 flex-1 items-center justify-between gap-2"
                              >
                                <div className="flex items-center gap-1.5 min-w-0">
                                  {rowStop && (
                                    <MapPin
                                      className="h-3.5 w-3.5 shrink-0"
                                      style={{ color: driver.color }}
                                    />
                                  )}
                                  <span className="truncate font-medium block">
                                    {rowLabel(task, rowStop) || t('routePlanning.unknownOrder')}
                                  </span>
                                  {task.stops.length === 0 && (
                                    <UnlinkedReason className="shrink-0 rounded bg-muted px-1.5 py-0.5" />
                                  )}
                                </div>

                                <div className="flex items-center gap-2 shrink-0 text-right">
                                  {task.state === 'completed' && task.completedAt ? (
                                    <span className="tabular-nums text-muted-foreground">
                                      {formatTime(task.completedAt)}
                                    </span>
                                  ) : task.estimatedCompletionTime ? (
                                    <div className="flex flex-col items-end">
                                      <span className="tabular-nums text-foreground">
                                        {formatTime(task.estimatedCompletionTime)}
                                      </span>
                                      {relEta && (
                                        <span
                                          className={`text-[10px] ${
                                            relEta.overdue
                                              ? 'text-destructive font-medium'
                                              : 'text-muted-foreground'
                                          }`}
                                        >
                                          {relEta.text}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}

                                  <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] text-foreground">
                                    <span
                                      aria-hidden="true"
                                      className={`h-1.5 w-1.5 shrink-0 rounded-full ${TASK_STATE_DOT[task.state]}`}
                                    />
                                    {t(`dispatch.eta.state.${task.state}`)}
                                  </span>
                                </div>
                              </div>

                              {rowStop && <JobLink jobId={rowStop.job_id} />}
                            </div>

                            {/* One Onfleet task, N delivery jobs (ADR-0026 D2).
                              Each member is its own delivery: its own stop on
                              the map, and its own delivery-job link. */}
                            {isConsolidated &&
                              task.stops.map((memberStop) => {
                                const selectMember = onSelectStop
                                  ? () => onSelectStop(memberStop, driver.workerId)
                                  : null;
                                const isMemberSelected =
                                  selectedStop && memberStop.job_id === selectedStop.job_id;

                                return (
                                  <div
                                    key={memberStop.job_id}
                                    data-testid="member-row"
                                    className={`flex items-center gap-2 py-2 pl-7 pr-2.5 transition-colors ${
                                      selectMember ? 'cursor-pointer hover:bg-accent/15' : ''
                                    } ${isMemberSelected ? 'bg-accent/25 font-medium' : ''}`}
                                  >
                                    {/* A member row is part of its task's one
                                      call, not a call of its own: it carries a
                                      continuation mark where its parent
                                      carries a number, and it keeps the indent
                                      (#2251) that makes the two read as nested
                                      — the mark alone would leave a member's
                                      label starting exactly where its parent's
                                      does. Numbering the members would make
                                      the driver's "5th call" uncountable. */}
                                    <span
                                      aria-hidden="true"
                                      className="w-5 shrink-0 text-right text-[11px] leading-none text-muted-foreground"
                                    >
                                      ↳
                                    </span>

                                    <div
                                      {...activationProps(selectMember)}
                                      className="flex min-w-0 flex-1 items-center gap-1.5"
                                    >
                                      <MapPin
                                        className="h-3.5 w-3.5 shrink-0"
                                        style={{ color: driver.color }}
                                      />
                                      <span className="truncate font-medium block">
                                        {rowLabel(task, memberStop) ||
                                          t('routePlanning.unknownOrder')}
                                      </span>
                                    </div>

                                    <JobLink jobId={memberStop.job_id} />
                                  </div>
                                );
                              })}
                          </Fragment>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 5. Stops nobody is carrying. Not the same thing as the section below:
             these geocoded fine and are on the map as grey dots — they simply
             have no driver, and until now the list side never said so. On a
             past date every stop looks like this, which is what the notice at
             the top already says, so the group stays away there. While the
             operator is searching it lists the matches, exactly as the driver
             results above do: one question, one set of answers. */}
      {isToday && unassignedMatches.length > 0 && (
        <details className="group p-4 space-y-3" open={unassignedMatches.length <= 5}>
          <summary className="flex cursor-pointer list-none items-center justify-between gap-2 font-medium text-sm [&::-webkit-details-marker]:hidden">
            <div className="flex items-center gap-2 text-foreground">
              <MapPin className="h-4 w-4" style={{ color: UNASSIGNED_COLOR }} aria-hidden="true" />
              <span>{t('routePlanning.unassigned.title')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                {t('routePlanning.unassignedCount', {
                  count: unassignedMatches.length,
                })}
              </span>
              <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-90" />
            </div>
          </summary>

          <ul className="divide-y text-xs pt-1">
            {unassignedMatches.map((stop) => {
              // No driver owns this stop, so none travels with it: a driver id
              // here would select a driver who does not have it.
              const selectStop = onSelectStop ? () => onSelectStop(stop) : null;

              return (
                <li key={stop.job_id} className="first:pt-1">
                  <div
                    {...activationProps(selectStop)}
                    className={`flex items-center justify-between gap-2 py-2.5 transition-colors ${
                      selectStop ? 'cursor-pointer hover:bg-accent/15' : ''
                    }`}
                  >
                    <span className="truncate font-medium text-foreground">
                      {stop.order_name || t('routePlanning.unknownOrder')}
                    </span>
                    <span className="shrink-0 text-muted-foreground">{stop.zip}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </details>
      )}

      {/* 6. Missing coordinates section */}
      {missing.length > 0 && (
        <details className="group p-4 space-y-3" open={missing.length <= 5}>
          <summary className="flex cursor-pointer list-none items-center justify-between gap-2 font-medium text-sm [&::-webkit-details-marker]:hidden">
            <div className="flex items-center gap-2 text-foreground">
              <AlertCircle className="h-4 w-4 text-warning" />
              <span>{t('routePlanning.missing.title')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                {t('routePlanning.missingCount', { count: missing.length })}
              </span>
              <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-90" />
            </div>
          </summary>

          <ul className="divide-y text-xs pt-1">
            {missing.map((job) => (
              <li key={job.job_id} className="py-2.5 first:pt-1">
                <p className="font-medium text-foreground">
                  {job.order_name || t('routePlanning.unknownOrder')}
                </p>
                <p className="text-muted-foreground mt-0.5">
                  {t(MISSING_REASON_LABELS[job.reason])}
                </p>
              </li>
            ))}
          </ul>
        </details>
      )}
    </aside>
  );
}
